import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getPinsForRef,
  getPinThread,
} from "../../../../../services/inspirationsService";

const PIN_REF_TYPE = "inspiration";

const formatDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return "";
  }
};

const userInitial = (u) => {
  const n = u?.name || u?.user_name || "";
  return n ? n.charAt(0).toUpperCase() : "?";
};

const PinMarker = ({ pin, onClick, selected }) => {
  const isResolved = (pin.status || "").toUpperCase() === "RESOLVED";
  const bg = isResolved ? "#10B981" : "#4D6BF6";
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick(pin);
      }}
      sx={{
        position: "absolute",
        left: `${(pin.x_pos || 0) * 100}%`,
        top: `${(pin.y_pos || 0) * 100}%`,
        transform: "translate(-50%, -50%)",
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: bg,
        border: selected ? "2px solid #fff" : "2px solid rgba(255,255,255,0.7)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
        cursor: "pointer",
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {pin.thread_count || ""}
    </Box>
  );
};

const CommentBubble = ({ comment }) => (
  <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
    <Avatar
      sx={{
        width: 32,
        height: 32,
        backgroundColor: "#EEF2FF",
        color: "#4D6BF6",
        fontSize: 13,
      }}
    >
      {userInitial(comment.user_data || comment.user)}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {comment.user_data?.name || comment.user?.name || "User"}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
          {formatDate(comment.created_at || comment.createdAt)}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          color: "#0D0D0E",
          whiteSpace: "pre-wrap",
          mt: 0.5,
        }}
      >
        {comment.body}
      </Typography>
      {Array.isArray(comment.attachments) &&
        comment.attachments.map((att, i) => {
          const url = att?.path || att?.url;
          const isImg = (att?.mime_type || att?.type || "").includes("image");
          if (!url) return null;
          if (isImg) {
            return (
              <Box
                key={i}
                component="img"
                src={url}
                alt=""
                sx={{
                  display: "block",
                  mt: 1,
                  maxWidth: 180,
                  maxHeight: 100,
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
            );
          }
          return (
            <Box
              key={i}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-block",
                mt: 1,
                fontSize: 12,
                color: "#4D6BF6",
                textDecoration: "underline",
              }}
            >
              {att.title || att.file_name || "View attachment"}
            </Box>
          );
        })}
    </Box>
  </Box>
);

const PinThread = ({ pinId, onBack }) => {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getPinThread(pinId);
        if (cancelled) return;
        setThread(res?.success ? res.data : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pinId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!thread) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
          Thread not found.
        </Typography>
      </Box>
    );
  }

  const isResolved = (thread.status || "").toUpperCase() === "RESOLVED";
  const replies = thread.replies || thread.comments || [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          Thread
        </Typography>
        <Chip
          size="small"
          label={isResolved ? "Resolved" : "Open"}
          sx={{
            backgroundColor: isResolved ? "#D1FAE5" : "#DBEAFE",
            color: isResolved ? "#065F46" : "#1E40AF",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        <CommentBubble comment={thread} />
        {replies.map((r) => (
          <Box key={r.id} sx={{ ml: 4 }}>
            <CommentBubble comment={r} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const MoodboardImageModal = ({ open, onClose, images, initialIndex }) => {
  const [index, setIndex] = useState(initialIndex || 0);
  const [pins, setPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);

  useEffect(() => {
    if (open) setIndex(initialIndex || 0);
  }, [open, initialIndex]);

  const currentImage = images?.[index];

  useEffect(() => {
    if (!open || !currentImage?.id) return;
    let cancelled = false;
    setActiveThreadId(null);
    (async () => {
      setPinsLoading(true);
      try {
        const res = await getPinsForRef(PIN_REF_TYPE, currentImage.id);
        if (cancelled) return;
        setPins(
          res?.success && Array.isArray(res.data) ? res.data : []
        );
      } finally {
        if (!cancelled) setPinsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentImage?.id]);

  const handleKey = (e) => {
    if (e.key === "Escape") {
      if (activeThreadId) setActiveThreadId(null);
      else onClose();
    }
    if (!activeThreadId && images?.length > 1) {
      if (e.key === "ArrowLeft" && index > 0) setIndex(index - 1);
      if (e.key === "ArrowRight" && index < images.length - 1)
        setIndex(index + 1);
    }
  };

  if (!open || !currentImage) return null;
  const imgUrl =
    currentImage.image_thumbnail || currentImage.url || currentImage.path;

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus>
      <Box
        onKeyDown={handleKey}
        tabIndex={-1}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            zIndex: 20,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            backgroundColor: "#0D0D0E",
            borderRadius: 2,
            overflow: "hidden",
            width: "95vw",
            maxWidth: 1280,
            height: "85vh",
            position: "relative",
          }}
        >
          {/* Image side */}
          <Box
            sx={{
              flex: 1,
              position: "relative",
              backgroundColor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {images.length > 1 && index > 0 && (
              <IconButton
                onClick={() => setIndex(index - 1)}
                sx={{
                  position: "absolute",
                  left: 8,
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  zIndex: 4,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}
            {images.length > 1 && index < images.length - 1 && (
              <IconButton
                onClick={() => setIndex(index + 1)}
                sx={{
                  position: "absolute",
                  right: 8,
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  zIndex: 4,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            )}

            <Box
              sx={{
                position: "relative",
                maxWidth: "100%",
                maxHeight: "100%",
                userSelect: "none",
              }}
            >
              <Box
                component="img"
                src={imgUrl}
                alt=""
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "85vh",
                  objectFit: "contain",
                }}
              />
              {pins.map((p) => (
                <PinMarker
                  key={p.id}
                  pin={p}
                  onClick={() => setActiveThreadId(p.id)}
                  selected={activeThreadId === p.id}
                />
              ))}
            </Box>

            {images.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 12,
                  color: "#fff",
                  fontSize: 12,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                {index + 1} / {images.length}
              </Box>
            )}
          </Box>

          {/* Side panel */}
          <Box
            sx={{
              width: 380,
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeThreadId ? (
              <PinThread
                pinId={activeThreadId}
                onBack={() => setActiveThreadId(null)}
              />
            ) : (
              <>
                <Box
                  sx={{
                    p: 2,
                    borderBottom: "1px solid #E5E7EB",
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    Comments
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.5 }}>
                    Click a pin on the image to view its thread.
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                  {pinsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  ) : pins.length === 0 ? (
                    <Typography
                      sx={{
                        color: "#6B7280",
                        fontSize: 13,
                        textAlign: "center",
                      }}
                    >
                      No comment threads on this image.
                    </Typography>
                  ) : (
                    pins.map((p) => {
                      const isResolved =
                        (p.status || "").toUpperCase() === "RESOLVED";
                      return (
                        <Box
                          key={p.id}
                          onClick={() => setActiveThreadId(p.id)}
                          sx={{
                            p: 1.5,
                            border: "1px solid #E5E7EB",
                            borderRadius: 1,
                            mb: 1,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#F9FAFB" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: "#EEF2FF",
                                color: "#4D6BF6",
                                fontSize: 11,
                              }}
                            >
                              {userInitial(p.user_data || p.user)}
                            </Avatar>
                            <Typography
                              sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}
                            >
                              {p.user_data?.name || p.user?.name || "User"}
                            </Typography>
                            <Chip
                              size="small"
                              label={isResolved ? "Resolved" : "Open"}
                              sx={{
                                backgroundColor: isResolved
                                  ? "#D1FAE5"
                                  : "#DBEAFE",
                                color: isResolved ? "#065F46" : "#1E40AF",
                                fontSize: 10,
                                height: 18,
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#374151",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {p.body}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MoodboardImageModal;
